import React, { useEffect, useRef, useState } from 'react';
import { string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { Form, FieldTextInput, SecondaryButtonInline, IconSpinner } from '../../../components';
import { onTriggerError } from '../../../util/toast';
import { messageSendErrorId } from '../../../util/messageSending';

import css from './SendMessageForm.module.css';
import IconAttachment from '../../../components/IconAttachment/IconAttachment';

const BLUR_TIMEOUT_MS = 100;

const IconSendMessage = () => (
  <svg
    className={css.sendIcon}
    width="14"
    height="14"
    viewBox="0 0 14 14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g className={css.strokeMatter} fill="none" fillRule="evenodd" strokeLinejoin="round">
      <path d="M12.91 1L0 7.003l5.052 2.212z" />
      <path d="M10.75 11.686L5.042 9.222l7.928-8.198z" />
      <path d="M5.417 8.583v4.695l2.273-2.852" />
    </g>
  </svg>
);

const SendMessageFormComponent = props => {
  const {
    rootClassName = null,
    className = null,
    messagePlaceholder = null,
    inProgress = false,
    sendMessageError = null,
    onFocus = null,
    onBlur = null,
    onUploadImageViaSharetribe = null,
    onSubmit,
    formId,
    intl,
  } = props;

  const blurTimeoutId = useRef(null);
  const fileInputRef = useRef(null);
  const submissionInProgress = useRef(false);

  const [uploadedAttachmentsUrls, setUploadedAttachmentsUrls] = useState([]);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState(false);

  useEffect(() => () => window.clearTimeout(blurTimeoutId.current), []);

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
    window.clearTimeout(blurTimeoutId.current);
  };

  const handleBlur = () => {
    blurTimeoutId.current = window.setTimeout(() => {
      if (onBlur) {
        onBlur();
      }
    }, BLUR_TIMEOUT_MS);
  };

  const removeAttachment = index => {
    setUploadedAttachmentsUrls(prev => prev.filter((_, i) => i !== index));
  };

  const onAttachmentUpload = async event => {
    const rawFiles = Array.from(event.target.files || []);
    const files = rawFiles.filter(file => file.size > 0);

    if (rawFiles.length > 0 && files.length < rawFiles.length) {
      onTriggerError(
        intl.formatMessage({ id: 'SendMessageForm.attachmentUploadFailedInvalidContent' })
      );
    }

    if (!files.length || typeof onUploadImageViaSharetribe !== 'function') {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploadInProgress(true);
    setUploadError(false);

    try {
      for (const file of files) {
        const imageLink = await onUploadImageViaSharetribe(file);
        if (imageLink) {
          setUploadedAttachmentsUrls(prev => [...prev, { link: imageLink, name: file.name }]);
        }
      }
    } catch (_error) {
      setUploadError(true);
    } finally {
      setUploadInProgress(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetAttachments = form => {
    setUploadedAttachmentsUrls([]);
    form.change('attachmentsUrls', []);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFormSubmit = async (values, form) => {
    if (submissionInProgress.current || inProgress || uploadInProgress) return;
    if (!values.message?.trim() && !uploadedAttachmentsUrls.length) return;
    submissionInProgress.current = true;
    const payload = {
      ...values,
      attachmentsUrls: uploadedAttachmentsUrls,
    };

    try {
      await onSubmit(payload, form, {
        onAttachmentSent: attachment => {
          setUploadedAttachmentsUrls(previous => previous.filter(item => item !== attachment));
        },
      });
      resetAttachments(form);
    } catch (error) {
      return { [FORM_ERROR]: messageSendErrorId(error) };
    } finally {
      submissionInProgress.current = false;
    }
  };

  return (
    <FinalForm
      onSubmit={handleFormSubmit}
      render={({ handleSubmit, hasValidationErrors, submitting, submitError, values }) => {
        const classes = classNames(rootClassName || css.root, className);
        const sending = inProgress || submitting;
        const submitDisabled =
          hasValidationErrors ||
          sending ||
          uploadInProgress ||
          (!values.message?.trim() && !uploadedAttachmentsUrls.length);
        const errorId = submitError || (sendMessageError && messageSendErrorId(sendMessageError));

        return (
          <Form className={classes} onSubmit={handleSubmit} aria-busy={sending}>
            {uploadedAttachmentsUrls.length ? (
              <div className={css.uploadedImage}>
                {uploadedAttachmentsUrls.map((item, index) => (
                  <div key={`${item.link}-${index}`} className={css.image}>
                    <img
                      srcSet={item.link}
                      src={item.link}
                      alt={intl.formatMessage({ id: 'SendMessageForm.attachmentAlt' })}
                    />
                    <button
                      type="button"
                      className={css.removeAttachment}
                      disabled={sending}
                      onClick={() => removeAttachment(index)}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <div className={css.sendMessageWrapper}>
              <FieldTextInput
                inputRootClass={css.textarea}
                type="textarea"
                id={formId ? `${formId}.message` : 'message'}
                name="message"
                aria-label={intl.formatMessage({ id: 'MessagePanel.sendMessagePlaceholder' })}
                readOnly={sending}
                placeholder={messagePlaceholder}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />

              <div className={css.submitContainer}>
                {typeof onUploadImageViaSharetribe === 'function' ? (
                  <>
                    <input
                      ref={fileInputRef}
                      className={css.fileInput}
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      multiple
                      tabIndex={-1}
                      disabled={sending || uploadInProgress}
                      onChange={onAttachmentUpload}
                    />
                    <button
                      type="button"
                      className={css.attach}
                      aria-label={intl.formatMessage({ id: 'SendMessageForm.attachImage' })}
                      disabled={sending || uploadInProgress}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadInProgress ? <IconSpinner /> : <IconAttachment />}
                    </button>
                  </>
                ) : null}

                <SecondaryButtonInline
                  className={css.submitButton}
                  type="submit"
                  aria-label={intl.formatMessage({ id: 'SendMessageForm.sendMessage' })}
                  inProgress={sending}
                  disabled={submitDisabled}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  <IconSendMessage />
                </SecondaryButtonInline>
              </div>
            </div>
            {errorId ? (
              <p className={css.error} role="alert">
                <FormattedMessage id={errorId} />
              </p>
            ) : null}
            {uploadError ? (
              <p className={css.error} role="alert">
                <FormattedMessage id="SendMessageForm.attachmentUploadFailedInvalidContent" />
              </p>
            ) : null}
          </Form>
        );
      }}
    />
  );
};

SendMessageFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,
  messagePlaceholder: string,
  onSubmit: func.isRequired,
  onFocus: func,
  onBlur: func,
  onUploadImageViaSharetribe: func,
  sendMessageError: propTypes.error,
  intl: intlShape.isRequired,
};

const SendMessageForm = compose(injectIntl)(SendMessageFormComponent);

SendMessageForm.displayName = 'SendMessageForm';

export default SendMessageForm;
